import { snakeCase } from "lodash";
import { ModelType } from "~/enums/modelType";
import { ObjectModelPropertyRootType } from "~/enums/objectModelPropertyType";
import { DatetimeMode } from "~/models/datetimeMode";
import ObjectModel from "~/models/objectModel";
import VisualizationSource from "~/models/visualizations/visualizationSource";
import VisualizationSourceDataPoint from "./visualizationSourceDataPoint";
import ObjectModelProperty from "~/models/objectModelProperties/objectModelProperty";
import {DataPointType} from "~/enums/visualizationType";

export default class VisualizationSourceObjectModel extends VisualizationSource {
  dimensionTypes = [ObjectModelPropertyRootType.PickList, ObjectModelPropertyRootType.DateTime, ObjectModelPropertyRootType.Text]
  measureTypes = [ObjectModelPropertyRootType.Number, ObjectModelPropertyRootType.Aggregate, ObjectModelPropertyRootType.AutoIncrement, ObjectModelPropertyRootType.RelationshipCount, ObjectModelPropertyRootType.DerivedField]

  _objectModelDimensions: VisualizationSourceDataPoint[];
  _accountRelationshipDimensions: VisualizationSourceDataPoint[];
  _objectModelMeasures: VisualizationSourceDataPoint[];
  _builtinDimensions: VisualizationSourceDataPoint[];
  _builtinMeasures: VisualizationSourceDataPoint[];

  readonly friendly_type = ModelType.ObjectModel;

  source_client_id: string;

  constructor() {
    super();

  }

  get builtinDimensions(): VisualizationSourceDataPoint[] {
    if (this._builtinDimensions) return this._builtinDimensions

    this._builtinDimensions = [
      "Created On",
      "Last Updated On"
    ].map(title => new VisualizationSourceDataPoint({title: title, referenced_client_id: snakeCase("static_" + title), data_point_type: DataPointType.Dimension, source: this.source}, this.parent))

    return this._builtinDimensions
  }

  get builtinMeasures(): VisualizationSourceDataPoint[] {
    if (this._builtinMeasures) return this._builtinMeasures

    this._builtinMeasures = [
      "Number of Records"
    ].map(title => new VisualizationSourceDataPoint({title, referenced_client_id: snakeCase("static_" + title), data_point_type: DataPointType.Measure, source: this.source}, this.parent))

    return this._builtinMeasures
  }

  get objectModelDimensions(): VisualizationSourceDataPoint[] {
    if (this._objectModelDimensions) return this._objectModelDimensions;

    this._objectModelDimensions = this.source.indexedProperties
      .filter(e => e.is(...this.dimensionTypes))
      .filter(e => {
        if (e.property_type === ObjectModelPropertyRootType.DateTime) {
          return e.datetime_mode === DatetimeMode.Date || e.datetime_mode === DatetimeMode.Datetime;
        }

        return true;
      })
      .map(e => new VisualizationSourceDataPoint({title: e.label_title, referenced_client_id: e.client_id, data_point_type: DataPointType.Dimension, source: this.source}, this.parent))

    return this._objectModelDimensions
  }

  get accountRelationshipDimensions(): VisualizationSourceDataPoint[] {
    if (this._accountRelationshipDimensions) return this._accountRelationshipDimensions;

    this._accountRelationshipDimensions = this.source?.accountRelationsArray
      .map(ar => new VisualizationSourceDataPoint({title: ar.label_title, referenced_client_id: ar.client_id, data_point_type: DataPointType.Dimension, source: this.source}, this.parent));

    return this._accountRelationshipDimensions;
  }

  get objectModelMeasures(): VisualizationSourceDataPoint[] {
    if (this._objectModelMeasures) return this._objectModelMeasures;

    this._objectModelMeasures = this.source.indexedProperties
      .filter(e => e.is(...this.measureTypes))
      .map(e => new VisualizationSourceDataPoint({title: e.label_title, referenced_client_id: e.client_id, data_point_type: DataPointType.Measure, source: this.source}, this.parent))

    return this._objectModelMeasures
  }

  get source() {
    return ObjectModel.repo.find(this.source_client_id);
  }

  // account relationships, built-in and indexed picklist, dropdown, checkbox, radio & datetime fields/properties
  get dimensions(): VisualizationSourceDataPoint[] {
    return this.accountRelationshipDimensions.concat(this.builtinDimensions).concat(this.objectModelDimensions)
  }

  // built-in and indexed numeric fields/properties
  get measures(): VisualizationSourceDataPoint[] {
    return this.builtinMeasures.concat(this.objectModelMeasures)
  }

  get allMeasuresAndDimensions(): VisualizationSourceDataPoint[] {
    return this.objectModelDimensions.concat(this.objectModelMeasures).concat(this.accountRelationshipDimensions);
  }

  getIndexedElement(clientId: string): ObjectModelProperty | null {
    return this.source.indexedProperties.find(p => p.client_id === clientId);
  }
}
